import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@ironhack/design-system2/components/tab';
import { append, partition, pipe, propEq, update } from 'ramda';
import { type ReactElement } from 'react';

import { WRAPPER_PADDING } from '@/app/components/client/module-wrapper/wrapper-padding';
import { CohortSelectorCard } from '@/app/components/server/cohort-selector/components/cohort-selector-card';
import { type PdpParamsParsed } from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/page';
import { sendEvent } from '@/lib/gtm';
import { commonKeys } from '@/lib/utils';
import { type GaPageData, type WebCohort } from '@/types';

type Props = {
  cohorts: WebCohort[];
  cohortCardButtonText: string;
  fullTimeTabTitle?: string;
  gaPageData: GaPageData;
  hideSingleTab?: boolean;
  isPriceHidden: boolean | null;
  params: PdpParamsParsed;
  partTimeTabTitle?: string;
} & FlexProps;

export const CohortSelectorCarousel = (props: Props): ReactElement => {
  const {
    cohortCardButtonText,
    cohorts,
    fullTimeTabTitle,
    gaPageData,
    hideSingleTab = false,
    isPriceHidden,
    params,
    partTimeTabTitle,
    ...flexProps
  } = props;
  const { gaCategory, gaPageType } = gaPageData;
  const { language } = params;

  const [ftCohorts, ptCohorts] = partition<WebCohort>(propEq('format', 'ft'))(
    cohorts
  ) as [WebCohort[], WebCohort[]];
  const ftTitle = fullTimeTabTitle || commonKeys[language].ft;
  const ptTitle = partTimeTabTitle || commonKeys[language].pt;
  const tabs = [
    { code: 'ft', cohorts: ftCohorts, title: ftTitle },
    { code: 'pt', cohorts: ptCohorts, title: ptTitle },
  ].filter((tab) => tab.cohorts.length > 0);

  const shouldShowTabs = tabs.length > 1 || !hideSingleTab;

  const paddingX = pipe(
    update<number | null>(0, 0),
    append<number | null | string>('calc(50vw - 50%)')
  )(WRAPPER_PADDING);

  return (
    <Tabs width="full">
      {shouldShowTabs && (
        <TabList gap={3} mb={3}>
          {tabs.map(({ code: formatCode, title: tabTitle }, index) => (
            <Tab
              fontSize="l"
              key={`${tabTitle}-${index}`}
              onClick={() => {
                sendEvent({
                  eventAction: 'click format',
                  eventCategory: gaCategory,
                  eventLabel: formatCode,
                  pageType: gaPageType,
                });
              }}
              textTransform="uppercase"
            >
              {tabTitle}
            </Tab>
          ))}
        </TabList>
      )}
      <TabPanels>
        {tabs.map(({ title: tabTitle, cohorts: cohortList }, index) => (
          <TabPanel
            key={`${tabTitle}-${index}`}
            // ml/transform/width break from module wrapper container & padding
            ml={[null, null, '50%']}
            // my/py preserve the top & bottom card shadows
            my={-0.5}
            overflowX={[null, null, 'auto']}
            px={paddingX}
            py={0.5}
            transform={[null, null, 'translate3d(-50%, 0, 0)']}
            width={[null, null, '100vw']}
          >
            <Flex
              alignItems="center"
              flexDirection={['column', null, 'row']}
              gap={2}
              // "max-content" preserves the right padding when scrolling
              minWidth={[null, null, 'max-content']}
              {...flexProps}
            >
              {cohortList.map((cohort) => (
                <CohortSelectorCard
                  buttonText={cohortCardButtonText}
                  cohort={cohort}
                  isPriceHidden={isPriceHidden}
                  key={cohort.id}
                  language={language}
                  params={params}
                />
              ))}
            </Flex>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
